/**
 * Footer Component — Case Associates LP Override
 *
 * Duplicates the landing-page Footer with company-specific styling: Visit Website CTA
 * uses primary background, white border, and white text; "Professionally Managed By"
 * section removed; Multihub and Repli attribution retained.
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyField,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
// @ts-ignore
import { VibesitesImage } from '@vibesites/ui-primitives';
// @ts-ignore
import {
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
  multihubLogoMonoWhiteUrl,
  repliWhiteLogoUrl,
} from '@vibesites/assets';
import { cn } from '@vibesites/ui-primitives';

// ============================================================================
// Types & Helpers
// ============================================================================

function ensureAbsoluteUrl(url: string | undefined | null): string {
  if (!url || url === '#') return url || '#';
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed) || /^tel:/i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith('#') || trimmed.startsWith('/')) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

function buildCtaUrl(baseUrl: string | null, queryParams: string | null): string {
  const url = ensureAbsoluteUrl(baseUrl);
  if (!queryParams || queryParams.trim() === '' || queryParams.trim() === '?utm_source=') {
    return url;
  }
  const params = queryParams.trim().startsWith('?') ? queryParams.trim() : `?${queryParams.trim()}`;
  if (url.includes('?')) {
    return `${url}&${params.slice(1)}`;
  }
  return `${url}${params}`;
}

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface GroupedHours {
  label: string;
  hours: string;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const sorted = [...raw].sort((a, b) => {
    const ai = DAY_ORDER.indexOf(normalizeDay(a.day || ''));
    const bi = DAY_ORDER.indexOf(normalizeDay(b.day || ''));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const groups: GroupedHours[] = [];
  for (const entry of sorted) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    const dayLabel = DAY_LABELS[key] || entry.day;
    const isClosed = !hasValue(entry.open_time) && !hasValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
    } else {
      groups.push({ label: dayLabel, hours: hoursStr });
    }
  }
  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_secondary_logo', 'property_mono_white_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const ctaLinkSlotDef: SlotGroupDefinition = {
  label: 'Visit Website CTA',
  fields: {
    label: {
      label: 'CTA Text',
      type: 'text',
      defaultValue: 'Visit Website',
    },
    url: {
      label: 'CTA URL',
      type: 'url',
      defaultField: 'property_website_url',
      suggestedFields: ['property_website_url', 'property_apply_now_url'],
    },
    query_params: {
      label: 'URL Query Parameters',
      type: 'text',
      description: 'Query string appended to the URL, e.g. ?utm_medium=ppc&utm_source=google&utm_campaign=landing-page',
      defaultValue: '?utm_source=',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phone = useContentToken('footer.phone');
  const addressLine1 = useContentToken('footer.address_line1');
  const copyrightRaw = useContentToken('footer.copyright');
  const copyrightText = copyrightRaw?.replace(/© \d{4}/, `© ${new Date().getFullYear()}`) ?? copyrightRaw;
  const googlePlaceUrl = usePropertyField('property_google_place_url') as string | null;
  const city = usePropertyField('property_address_city') as string | null;
  const state = usePropertyField('property_address_state') as string | null;
  const zip = usePropertyField('property_address_zip') as string | null;
  const addressLine2 = [city, state].filter(Boolean).join(', ') + (zip ? ` ${zip}` : '');

  const logo = useSlotGroup('footer.logo', logoSlotDef);
  const ctaLink = useSlotGroup('footer.cta_link', ctaLinkSlotDef);

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });
  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  return (
    <footer className="bg-secondary text-secondary-foreground font-body">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-14">

          <div className="flex flex-col items-center sm:items-start gap-5 min-w-0">
            <EditableSlotGroup groupPath="footer.logo" definition={logoSlotDef}>
              <a href="/" className="block">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    className="h-20 w-auto max-w-[200px] object-contain"
                  />
                ) : (
                  <div className="font-heading text-2xl text-secondary-foreground">Our Community</div>
                )}
              </a>
            </EditableSlotGroup>

            <EditableSlotGroup groupPath="footer.cta_link" definition={ctaLinkSlotDef}>
              <a
                href={buildCtaUrl(ctaLink.url, ctaLink.query_params)}
                target={ctaLink._target || '_blank'}
                rel="noopener noreferrer"
                className={cn(
                  'inline-flex w-full max-w-[min(100%,280px)] sm:w-auto sm:max-w-none items-center justify-center rounded-md border-2 border-white',
                  'bg-primary px-6 py-3 font-body text-sm font-semibold text-white no-underline',
                  'transition-all duration-200 hover:opacity-90 hover:shadow-lg',
                )}
              >
                {ctaLink.label || 'Visit Website'}
              </a>
            </EditableSlotGroup>
          </div>

          <div className="flex flex-col items-center sm:items-start gap-5 text-center sm:text-left min-w-0">
            <div>
              <p className="font-body text-[11px] uppercase tracking-[0.15em] text-secondary-foreground/70 mb-1">Call Us</p>
              <EditableToken path="footer.phone" as="a" className="font-body text-sm text-secondary-foreground/80 hover:text-secondary-foreground transition-colors duration-200" {...{ href: `tel:${phone?.replace(/[^+\d]/g, '')}` }}>
                {formatPhone(phone)}
              </EditableToken>
            </div>

            <div>
              <p className="font-body text-[11px] uppercase tracking-[0.15em] text-secondary-foreground/70 mb-1">Visit Us</p>
              <a
                href={googlePlaceUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="font-body text-sm text-secondary-foreground/80 hover:text-secondary-foreground transition-colors duration-200 block break-words"
              >
                <EditableToken path="footer.address_line1" as="span" className="block">
                  {addressLine1}
                </EditableToken>
                {addressLine2 && (
                  <span className="block">{addressLine2}</span>
                )}
              </a>
            </div>
          </div>

          <div className="flex flex-col items-center sm:items-start text-center sm:text-left min-w-0 sm:col-span-2 lg:col-span-1">
            <p className="font-body text-[11px] uppercase tracking-[0.15em] text-secondary-foreground/70 mb-3">Office Hours</p>
            {groupedHours.length > 0 ? (
              <dl className="space-y-2 w-full max-w-[320px] mx-auto sm:mx-0">
                {groupedHours.map((group, index) => (
                  <div key={index} className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-4 font-body text-sm text-secondary-foreground/80 hover:text-secondary-foreground/90 transition-colors duration-200">
                    <dt className="shrink-0">{group.label}</dt>
                    <dd className="text-center sm:text-right shrink-0">{group.hours}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="font-body text-sm text-secondary-foreground/70">Office hours will be displayed here.</p>
            )}
          </div>

        </div>
      </div>

      <div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
          <div className="flex flex-col items-center gap-4">
            <div className="flex flex-wrap items-center justify-center gap-8 text-secondary-foreground/70">
              <FairHousingIcon size={24} color="currentColor" />
              <PetsAllowedIcon size={24} color="currentColor" />
              <WheelchairIcon size={24} color="currentColor" />
            </div>
            <nav className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 font-body text-xs text-secondary-foreground/70 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-secondary-foreground/90 [&_a]:transition-colors [&_a]:duration-200 [&_a]:no-underline text-center" aria-label="Legal links">
              <RichTextContent
                path="footer.accessibility_link"
                as="span"
                defaultValue="<p>Accessibility</p>"
              />
              <span aria-hidden="true">/</span>
              <RichTextContent
                path="footer.privacy_link"
                as="span"
                defaultValue="<p>Privacy Policy</p>"
              />
            </nav>
            <EditableToken path="footer.copyright" as="span" className="font-body text-xs text-secondary-foreground/70 text-center px-2">
              {copyrightText}
            </EditableToken>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 sm:gap-8 md:gap-10 items-start justify-items-center">
            <div className="flex flex-col items-center w-full max-w-xs">
              <span className="font-body text-[11px] uppercase tracking-[0.15em] text-secondary-foreground/60 mb-3 text-center">Powered by</span>
              <div className="flex items-center justify-center" style={{ height: '26px' }}>
                <a href="https://multihub.io" target="_blank" rel="noopener noreferrer" className="block hover:opacity-80 transition-opacity duration-200">
                  <VibesitesImage url={multihubLogoMonoWhiteUrl} alt="Multihub" style={{ height: '26px', width: 'auto' }} />
                </a>
              </div>
            </div>

            <div className="flex flex-col items-center w-full max-w-xs">
              <span className="font-body text-[11px] uppercase tracking-[0.15em] text-secondary-foreground/60 mb-3 text-center">Website Design by</span>
              <div className="flex items-center justify-center" style={{ height: '36px' }}>
                <a href="https://www.repli360.com/" target="_blank" rel="noopener noreferrer" className="block hover:opacity-80 transition-opacity duration-200">
                  <VibesitesImage url={repliWhiteLogoUrl} alt="Repli" style={{ height: '36px', width: 'auto' }} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

    </footer>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_phone',
      defaultValue: '(555) 555-5555',
    },
    'footer.address_line1': {
      label: 'Street Address',
      type: 'text',
      defaultField: 'property_address_1',
      defaultValue: '123 Main Street',
    },
    'footer.copyright': {
      label: 'Copyright',
      type: 'text',
      defaultValue: '© 2026 All Rights Reserved.',
    },
    'footer.accessibility_link': {
      label: 'Accessibility Link',
      type: 'richtext',
      defaultValue: '<p>Accessibility</p>',
    },
    'footer.privacy_link': {
      label: 'Privacy Policy Link',
      type: 'richtext',
      defaultValue: '<p>Privacy Policy</p>',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.cta_link': ctaLinkSlotDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
